import React, { useState } from 'react';
import { CheckSquare, Mail, Tag, Trash2, X, CheckCircle, XCircle, Loader } from 'lucide-react';

export default function BulkActionsBar({ selectedApplications, onAction, onClearSelection }) {
  const [showTagInput, setShowTagInput] = useState(false);
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('blue');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const colors = ['blue', 'green', 'yellow', 'red', 'purple', 'pink', 'indigo', 'gray'];

  const handleBulkStatus = async (status) => {
    if (!window.confirm(`Are you sure you want to ${status} ${selectedApplications.length} applications?`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://tradelink-kseg.onrender.com/api/bulk-actions/applications/status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          applicationIds: selectedApplications,
          status: status
        })
      });

      if (response.ok) {
        alert(`Successfully updated ${selectedApplications.length} applications`);
        onAction();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      alert('Failed to update applications');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkTag = async () => {
    if (!tagName.trim()) {
      alert('Please enter a tag name');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://tradelink-kseg.onrender.com/api/bulk-actions/applications/tags', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          applicationIds: selectedApplications,
          tagName: tagName,
          color: tagColor
        })
      });

      if (response.ok) {
        alert(`Successfully tagged ${selectedApplications.length} applications`);
        setTagName('');
        setShowTagInput(false);
        onAction();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      alert('Failed to add tags');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      alert('Please enter both subject and message');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://tradelink-kseg.onrender.com/api/bulk-email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          applicationIds: selectedApplications,
          subject: emailSubject,
          message: emailMessage
        })
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        setEmailSubject('');
        setEmailMessage('');
        setShowEmailModal(false);
        onAction();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      alert('Failed to send emails');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to DELETE ${selectedApplications.length} applications? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('https://tradelink-kseg.onrender.com/api/bulk-actions/applications', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          applicationIds: selectedApplications
        })
      });

      if (response.ok) {
        alert(`Successfully deleted ${selectedApplications.length} applications`);
        onAction();
      } else {
        const error = await response.json();
        alert(`Error: ${error.error}`);
      }
    } catch (error) {
      alert('Failed to delete applications');
    } finally {
      setLoading(false);
    }
  };

  if (selectedApplications.length === 0) return null;

  return (
    <>
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-white border-2 border-blue-500 rounded-lg shadow-2xl p-4 z-50 max-w-4xl w-full mx-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 font-semibold text-gray-700">
            <CheckSquare className="text-blue-600" size={20} />
            <span>{selectedApplications.length} selected</span>
          </div>

          <div className="h-8 w-px bg-gray-300" />

          <button
            onClick={() => handleBulkStatus('accepted')}
            disabled={loading}
            className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 transition text-sm font-medium"
          >
            <CheckCircle size={16} />
            Accept All
          </button>

          <button
            onClick={() => handleBulkStatus('rejected')}
            disabled={loading}
            className="flex items-center gap-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition text-sm font-medium"
          >
            <XCircle size={16} />
            Reject All
          </button>

          <button
            onClick={() => setShowTagInput(!showTagInput)}
            disabled={loading}
            className="flex items-center gap-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 transition text-sm font-medium"
          >
            <Tag size={16} />
            Tag All
          </button>

          <button
            onClick={() => setShowEmailModal(true)}
            disabled={loading}
            className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition text-sm font-medium"
          >
            <Mail size={16} />
            Email All
          </button>

          <button
            onClick={handleBulkDelete}
            disabled={loading}
            className="flex items-center gap-1 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-800 disabled:opacity-50 transition text-sm font-medium"
          >
            <Trash2 size={16} />
            Delete All
          </button>

          <div className="h-8 w-px bg-gray-300" />

          <button
            onClick={onClearSelection}
            className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-800 transition text-sm"
          >
            <X size={16} />
            Clear
          </button>

          {loading && <Loader className="animate-spin text-blue-600" size={20} />}
        </div>

        {/* Tag Input */}
        {showTagInput && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-semibold mb-3 text-sm">Add Tag to Selected Applications</h4>
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder="Tag name"
                value={tagName}
                onChange={(e) => setTagName(e.target.value)}
                className="flex-1 min-w-[200px] px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
              />
              <div className="flex gap-2">
                {colors.map(c => (
                  <button
                    key={c}
                    onClick={() => setTagColor(c)}
                    className={`w-8 h-8 rounded-full bg-${c}-500 ${tagColor === c ? 'ring-2 ring-offset-2 ring-gray-800' : ''}`}
                    title={c}
                  />
                ))}
              </div>
              <button
                onClick={handleBulkTag}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition text-sm font-medium"
              >
                Add Tag
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">Send Bulk Email</h3>
                <button onClick={() => setShowEmailModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recipients: {selectedApplications.length} candidates
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter email subject"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Enter your message"
                    rows={10}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleBulkEmail}
                    disabled={loading}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition font-medium flex items-center justify-center gap-2"
                  >
                    {loading ? <Loader className="animate-spin" size={20} /> : <Mail size={20} />}
                    Send Email
                  </button>
                  <button
                    onClick={() => setShowEmailModal(false)}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
